import _keyBy from 'lodash-es/keyBy';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  getJobsRequest,
  type JobsHttpGetJobsQueryPatched,
} from '@iconik-io/iconik-api/src/schema_patched/jobs';
import { handleErrors } from '@iconik-io/shared-lib/src/utils/handle_messages';
import { getSortQuery } from '@iconik-io/shared-lib/src/utils/sort/get_sort_query';

import {
  JOB_STATUS_FAILED,
  JOB_STATUS_STARTED,
  JOB_STATUS_WAITING,
} from '../../../constants/jobs';
import type { AppDispatch, GetState } from '../../../store/types';
import {
  addOneSecondToRightBoundary,
  addOneSecondToRightBoundaryString,
} from '../../../utils/add_one_second_to_right_boundary';
import { readFacetBuckets } from '../../../utils/read_facet_buckets';
import { guardFilterRange } from '../../../utils/search_utils/guard_filter';
import { commonListActions } from '../../common_list/slice';
import { wrapCommonGetList } from '../../common_list/thunks';
import { setJobWidgetStats } from '../../jobs_stats_widgets/slice';
import type { GetJobs } from '../types';

export const getJobs: GetJobs =
  (widget, params = {}, debounce = false) =>
  (dispatch: AppDispatch, getState: GetState) =>
    wrapCommonGetList({
      prefix: `admin_jobs_widgets_${widget.id}`,
      params,
      dispatch,
      debounce,
      getListPromise: (listParams) => {
        const state = getState();
        const { filters, limit } = widget.options;
        const listState = state.jobsWidgetsLists[widget.id];
        const filtersState = state.jobsWidgetsFilters[widget.id];
        let sort: string | undefined;
        if (listState) {
          sort = getSortQuery(listState.sort);
        } else {
          sort = widget.options.sort
            .map((s) => `${s.name},${s.order}`)
            .join(';');
        }
        const createdBy =
          (filters.my_jobs ? state.authReducer.userData.id : undefined) ||
          filters.created_by;

        const panelDateCreated = guardFilterRange(
          filtersState.filterValues.date_created,
        );
        const dateModified = guardFilterRange(
          filtersState.filterValues.date_modified,
        );

        const { date_modified, date_created, ...filterValues } =
          filtersState.filterValues;
        const body: JobsHttpGetJobsQueryPatched = {
          sort,
          page: listParams.page,
          per_page: limit,
          status: filters.status,
          type: filters.type,
          object_type: filters.object_type,
          created_by: createdBy,
          storage_id: filters.storage_id?.length
            ? filters.storage_id
            : undefined,
          facets: listParams.facets ?? false,
          date_created: panelDateCreated
            ? addOneSecondToRightBoundary(panelDateCreated)
            : addOneSecondToRightBoundaryString(filters.date_created),
          date_modified: dateModified
            ? addOneSecondToRightBoundary(dateModified)
            : undefined,
          query: filtersState.query,
          ...filterValues,
          ...filtersState.facetValues,
          'metadata.automation_id': filters.automation_id,
        };

        return dispatch(getJobsRequest(body)).catch((error) => {
          const actions = commonListActions(`admin_jobs_widgets_${widget.id}`);

          if (error instanceof EndpointError) {
            const errors = handleErrors(error);

            dispatch(
              actions.setLatestError({
                status: error.response.status,
                statusText: error.response.statusText,
                headers: error.response.headers,
                errors,
              }),
            );
          }
          throw error;
        });
      },
    });

export const getStatusStatsJobs: GetJobs =
  (widget) => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const { filters } = widget.options;
    return dispatch(
      getJobsRequest({
        type: filters.type,
        object_type: filters.object_type,
        date_created: filters.date_created
          ? addOneSecondToRightBoundaryString(filters.date_created)
          : undefined,
        created_by: filters.my_jobs ? state.authReducer.userData.id : undefined,
        aggregations: 'status',
        per_page: 1,
      }),
    )
      .then((response) => {
        const { body } = response;
        const statusStatsMap = _keyBy(
          readFacetBuckets(body.facets, 'status'),
          'key',
        );
        dispatch(
          setJobWidgetStats({
            widgetId: widget.id,
            stats: {
              waiting: statusStatsMap[JOB_STATUS_WAITING]?.doc_count ?? 0,
              active: statusStatsMap[JOB_STATUS_STARTED]?.doc_count ?? 0,
              failed: statusStatsMap[JOB_STATUS_FAILED]?.doc_count ?? 0,
              total: body.total,
            },
          }),
        );

        return response;
      })
      .catch((error) => {
        if (error instanceof EndpointError) {
          const errors = handleErrors(error);
          const actions = commonListActions(`admin_jobs_widgets_${widget.id}`);

          dispatch(
            actions.setLatestError({
              status: error.response.status,
              statusText: error.response.statusText,
              headers: error.response.headers,
              errors,
            }),
          );
        }
        throw error;
      });
  };
